import Column from 'antd/lib/table/Column';
import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { AppState } from 'src/config/appState';
import { StepButtons, StepButtonsProps } from 'src/modules/programs/components/StepButtons';
import { ProgramEnum } from 'src/modules/programs/models/programs';
import * as S from 'src/modules/programs/stepComponents/styles';
import {
    fetchProgramSurvey,
    submitProgramSurvey,
} from 'src/modules/programs/surveys/actions/surveysActions';
import {
    SurveyStepsQuestion,
    SurveyStepsQuestionAnswer,
} from 'src/modules/programs/surveys/models/surveys';
import {
    getProgramSurveyAnswers,
    getProgramSurveyQuestions,
    isProgramSurveyAnswered,
} from 'src/modules/programs/surveys/selectors/surveysSelectors';

import { HierarchyTableContent } from '../components/HierarchyTableContent';
import { CBTProgramWeekSevenSurvey } from '../models/weekSeven';

const surveyId = CBTProgramWeekSevenSurvey.YourExposureHierarchy;

const dataSource = [
    {
        key: 1,
    },
];

const columnsData = [
    {
        key: 'goal',
        dataIndex: 'goal',
        width: 450,
    },
    {
        key: 'level',
        dataIndex: 'level',
        width: 450,
    },
];

interface ActionsProps {
    submitProgramSurvey: typeof submitProgramSurvey;
    fetchProgramSurvey: typeof fetchProgramSurvey;
}

interface StateProps {
    questions: SurveyStepsQuestion[] | undefined;
    answers: SurveyStepsQuestionAnswer[] | undefined;
    isSurveyAnswered: boolean;
}

interface Props extends StateProps, ActionsProps, StepButtonsProps, WithTranslation {
    programId: ProgramEnum;
}

class YourExposureHierarchyStepContainer extends React.Component<Props> {
    public componentDidMount(): void {
        const { programId } = this.props;

        this.props.fetchProgramSurvey({
            programId,
            surveyId,
        });
    }

    private tableContent = () =>
        this.props.questions ? (
            <HierarchyTableContent
                programId={this.props.programId}
                surveyId={surveyId}
                questions={this.props.questions}
            />
        ) : null;

    private handleSubmit = () => {
        const { programId, answers, goToNextStep } = this.props;

        if (!answers) {
            return;
        }

        this.props.submitProgramSurvey({
            programId,
            surveyId,
            answers,
        });

        goToNextStep();
    };

    public render(): JSX.Element {
        const { goToPreviousStep, isSurveyAnswered, t } = this.props;

        const columnsTranslations: Column<{}>[] = t(
            'week-7.your-exposure-hierarchy.exposureHierarchyTable.columns',
            {
                returnObjects: true,
            },
        );

        const columns = columnsData.map((columnData, index: number) => ({
            title: columnsTranslations[index],
            ...columnData,
        }));

        return (
            <>
                <S.ContentRow>
                    <S.ContentColumn>
                        <S.StepHeading>{t('week-7.your-exposure-hierarchy.title')}</S.StepHeading>
                        <S.StepDescription small>
                            {t('week-7.your-exposure-hierarchy.description')}
                        </S.StepDescription>
                        <S.StyledTable
                            components={{
                                body: {
                                    row: this.tableContent,
                                },
                            }}
                            columns={columns}
                            dataSource={dataSource}
                            pagination={false}
                        />
                    </S.ContentColumn>
                </S.ContentRow>
                <StepButtons
                    onPreviousClick={goToPreviousStep}
                    onNextClick={this.handleSubmit}
                    nextDisabled={!isSurveyAnswered}
                    hasNextStep
                />
            </>
        );
    }
}

const mapStateToProps = (state: AppState, { programId }: Props): StateProps => ({
    answers: getProgramSurveyAnswers(state, programId, surveyId) as
        | SurveyStepsQuestionAnswer[]
        | undefined,
    isSurveyAnswered: isProgramSurveyAnswered(state, programId, surveyId),
    questions: getProgramSurveyQuestions(state, programId, surveyId) as
        | SurveyStepsQuestion[]
        | undefined,
});

export const YourExposureHierarchyStep = withTranslation('cbt')(
    connect<StateProps>(mapStateToProps, { submitProgramSurvey, fetchProgramSurvey })(
        YourExposureHierarchyStepContainer,
    ),
);
